import compact from 'lodash/compact'
import intersection from 'lodash/intersection'
import { default as orderByLodash } from 'lodash/orderBy'
import moment from 'moment'

import type FiltersForm from 'modules/core/lib/forms/Filters'
import { normalizeText } from 'modules/core/lib/stringUtils'
import users, {
  employeeUser,
  isEmployeeTimeoffManagedByCurrent,
} from 'modules/core/store/collections/users'
import type User from 'modules/core/store/models/User'
import { IUser } from 'modules/core/store/models/User'

import presences, { employeeClockedIn } from 'modules/attendance/store/collections/presences'
import { employeeReferenceContract } from 'modules/contracts/lib/utils'
import contractVersions from 'modules/contracts/store/collections/contractVersions'
import hasRegularAccess from 'modules/employees/lib/hasRegularAccess'
import employeeGroups from 'modules/employees/store/collections/employeeGroups'
import employeesCollection from 'modules/employees/store/collections/employees'
import { employeeManagedEmployees } from 'modules/employees/store/collections/employeesContractVersions'
import type Employee from 'modules/employees/store/models/Employee'
import { IEmployee } from 'modules/employees/store/models/Employee'
import levels from 'modules/job_catalog/store/collections/levels'
import roles, { employeeJob } from 'modules/job_catalog/store/collections/roles'
import memberships from 'modules/teams/store/collections/memberships'
import leaves from 'modules/timeoff/store/collections/leaves'
import type Leave from 'modules/timeoff/store/models/Leave'

type FilterBy =
  | 'all'
  | 'away'
  | 'clocked_in'
  | 'reporting_to_me'
  | 'reporting_to_me_directly'
  | 'terminated'
  | 'timeoff_managed_by_me'
  | 'location_issues'
  | 'active'
  | 'with_welcome_space_access'

type FilterType =
  | 'activity'
  | 'employees'
  | 'gender'
  | 'location'
  | 'status'
  | 'team'
  | 'legalEntity'

interface FilterOptions {
  search?: string
}

const leaveEmployee = (leave: Leave) => {
  return employeesCollection.relatedTo(leave)
}

export const applyFiltersForUsers = async (
  filtersForm: FiltersForm,
  inputUsers: User[],
  valueFilterBy: string,
  options: FilterOptions = {}
) => {
  const usersWithoutEmployee = inputUsers.filter((u) => {
    const employee = u.isNew
      ? undefined
      : employeesCollection.find({
          access_id: u.attributes.id,
        })
    return !employee
  })

  const usersWithEmployee = (
    await filterEmployees(
      filtersForm,
      compact(
        inputUsers.map((u) => {
          return u.isNew
            ? undefined
            : employeesCollection.find({
                access_id: u.attributes.id,
              })
        })
      ),
      options
    )
  ).map((e) => employeeUser(e))

  if (valueFilterBy.startsWith('activity') && valueFilterBy.endsWith('active')) {
    return [...usersWithoutEmployee, ...usersWithEmployee]
  }

  return [...usersWithEmployee]
}

/**
 * Filter and sort by the fields
 */
export const applyFilters = async (
  filtersForm: FiltersForm,
  employees: Employee[],
  options: FilterOptions = {}
): Promise<Employee[]> => {
  const filteredEmployees = await filterEmployees(filtersForm, employees, options)
  return orderBy(filtersForm, filteredEmployees)
}

/**
 * returns list of attributes according to which the list of employees will be
 * sorted when 'status' option is selected
 */
const orderByStatus = (employee: Employee): boolean[] => {
  const defaultOrder = [
    employeeUser(employee).attributes.invited,
    employeeUser(employee).attributes.confirmed,
  ]

  return defaultOrder
}
/**
 * Filters the employees according to FilterOptions
 *
 * TODO: Smells like a refactor
 */
const filterEmployees = async (
  filtersForm: FiltersForm,
  employees: Employee[],
  options: FilterOptions = {}
): Promise<Employee[]> => {
  // We set a default type and filterBy value in case of the one from the local Storage was not a string.
  let type: FilterType | '' = ''
  let filterBy: FilterBy | '' = ''

  if (typeof filtersForm.get('filterBy').value === 'string') {
    const [localStorageType, localStorageFilterBy]: [FilterType, FilterBy] = filtersForm
      .get('filterBy')
      .value.split('___')
    type = localStorageType
    filterBy = localStorageFilterBy
  }

  let legalEntityId: number
  let teamId: string
  let managerId: number
  let activity: FilterBy | ''
  let genderId: string
  let locationId: string
  let employeeIds: number[]

  await users.fetch({ withCachedFetchParams: true })

  switch (type) {
    case 'legalEntity':
      await employeeGroups.fetch({ withCachedFetchParams: true })

      legalEntityId = parseInt(filterBy)
      employeeIds = employees
        .filter((employee) => {
          const group = employeeGroups.find({
            id: employee.attributes.employee_group_id,
          })

          if (!group) throw Error('Not found employee group')

          return group.attributes.legal_entity_id === legalEntityId
        })
        .map((employee) => employee.attributes.id)
      break

    case 'team':
      await memberships.fetch({ withCachedFetchParams: true })

      teamId = filterBy
      employeeIds = memberships
        .filter({
          team_id: Number(teamId),
        })
        .map((membership) => membership.attributes.employee_id)
      break

    case 'location':
      locationId = filterBy
      employeeIds = employees
        .filter((employee) => employee.attributes.location_id === parseInt(locationId))
        .map((employee) => employee.attributes.id)

      break

    case 'gender':
      genderId = filterBy
      employeeIds = employees
        .filter((employee) => {
          const employeeGender = employee.attributes.gender

          if (!employeeGender && genderId === 'no_gender') return true

          return employeeGender === genderId
        })
        .map((employee) => employee.attributes.id)

      break

    case 'employees':
      {
        const currentUser = users.current
        const currentEmployee = currentUser.isNew
          ? undefined
          : employeesCollection.find({
              access_id: currentUser.attributes.id,
            })

        if (filterBy === 'reporting_to_me_directly' && currentEmployee) {
          managerId = currentEmployee.attributes.id
          employeeIds = employees
            .filter((employee) => employee.attributes.manager_id === managerId)
            .map((employee) => employee.attributes.id)
        }

        if (filterBy === 'reporting_to_me' && currentEmployee) {
          await contractVersions.fetch({ withCachedFetchParams: true })

          managerId = currentEmployee.attributes.id
          employeeIds = employeeManagedEmployees(currentEmployee, contractVersions).map(
            (employee) => employee.attributes.id
          )
        }

        if (filterBy === 'timeoff_managed_by_me' && currentEmployee) {
          employeeIds = employees
            .filter((employee) => isEmployeeTimeoffManagedByCurrent(employee, employeesCollection))
            .map((employee) => employee.attributes.id)
        }
      }

      if (filterBy === 'away') {
        const today = moment().startOf('day')

        await leaves.fetch({ withCachedFetchParams: true })

        const todayAbsentees = leaves.models
          .filter((leave) => {
            const start = leave.get('start_on')
            const finish = leave.get('finish_on')

            if (!finish) {
              return today.isSameOrAfter(start)
            }

            return today.isSameOrAfter(start) && today.isSameOrBefore(finish)
          })
          .map((leave) => leaveEmployee(leave))
        employeeIds = intersection(employees, todayAbsentees).map(
          (employee) => employee.attributes.id
        )
      }

      if (filterBy === 'with_welcome_space_access') {
        employeeIds = employees
          .filter((e) => !hasRegularAccess(e.attributes.has_regular_access))
          .map((employee) => employee.attributes.id)
      }

      break

    case 'activity':
      activity = filterBy
      break

    case 'status':
      await presences.fetch({ withCachedFetchParams: true })

      // We only have the clocked in state for now.
      employeeIds = employees
        .filter((employee) => employeeClockedIn(employee))
        .map((employee) => employee.attributes.id)
      break

    default: // do nothing
  }

  const filterOptions: FilterBy[] = ['away', 'clocked_in', 'with_welcome_space_access']

  return employees.filter((employee) => {
    // TODO: Refactor this madness
    const eligibleForExclusion =
      locationId || managerId || (filterBy !== '' && filterOptions.includes(filterBy))

    const checkTerminated = () => {
      const active = activity === 'active' || !activity

      return employee.isTerminated && active
    }

    if (!employeeIds?.includes(employee.attributes.id) && (genderId || teamId || legalEntityId)) {
      return false
    }

    if (
      !employeeIds?.includes(employee.attributes.id) &&
      (eligibleForExclusion || filterBy === 'timeoff_managed_by_me')
    ) {
      return false
    }

    // By default this form always filter terminated employees
    if (checkTerminated()) return false

    if (activity === 'terminated' && !employee.isTerminated) {
      return false
    }

    return employeeUser(employee).matchesFullName(options.search || '')
  })
}

/**
 * Orders the array of employees according to the `sortBy` field
 */
export const orderBy = async (
  filtersForm: FiltersForm,
  employees: Employee[]
): Promise<Employee[]> => {
  const sortAttribute = filtersForm.get('sortAttribute').value
  const sortDirection = filtersForm.get('sortDirection').value

  const SortAttributes = {
    status: 'status',
    starts_on: 'starts_on',
    job_title: 'job_title',
    job: 'job',
  }

  if (!sortAttribute) {
    return employees
  }

  if (sortAttribute === SortAttributes.starts_on) {
    await contractVersions.fetch({ withCachedFetchParams: true })
  }

  if (sortAttribute === SortAttributes.job) {
    await levels.fetch({ withCachedFetchParams: true })
    await roles.fetch({ withCachedFetchParams: true })
  }

  return orderByLodash(
    employees,
    [
      (employee: Employee) => {
        if (sortAttribute === SortAttributes.starts_on) {
          return employeeReferenceContract(contractVersions, employee)?.attributes.starts_on
        }

        if (sortAttribute === SortAttributes.status) {
          return orderByStatus(employee)
        }

        if (sortAttribute === SortAttributes.job_title) {
          return employeeReferenceContract(
            contractVersions,
            employee
          )?.attributes.job_title?.toLowerCase()
        }

        if (sortAttribute === SortAttributes.job) {
          return employeeJob(contractVersions, employee)?.toLowerCase()
        }

        if (employee.has(sortAttribute)) {
          const attr = sortAttribute as keyof IEmployee
          const value = employee.attributes[attr]

          if (typeof value === 'string') {
            return normalizeText(value) || ''
          }

          return ''
        }

        const attr = sortAttribute as keyof IUser
        const value = employeeUser(employee).attributes[attr]

        if (typeof value === 'string') {
          return normalizeText(value) || ''
        }

        return ''
      },
    ],
    [sortDirection]
  )
}
